/**
 * seriesUtils：将后端返回的 ISeries 数组转换为 ECharts series 与 legendData。
 * - 统一处理对照组（controlName）与对比日期（compare）下的图例文案与颜色方案；
 * - 对 tooltip 所需的 TSeriesData 结构做一次性归一化，支持 percent/others 等特殊场景。
 */
import type { ISeries, TSeriesData } from './types';
import type { SeriesOption } from 'echarts/types/dist/option';
import { DATALAB_SPLIT_STR } from '@dbl/core/global';
import {
  buildLegendKey,
  buildLegendPaletteIndexMap,
  findRgbaForLegend,
  setRgbaAlpha,
} from '@/pages/datalab/utils/chartLegend';

type TLegendColors = Record<string, string>;

// Series item 基础类型（基于 ECharts SeriesOption 扩展）
type TSeriesItemStyle = Record<string, unknown> & { color: string; };
type TSeriesEmphasis = Record<string, unknown>;

/** 折线 series hover 时圆点高亮样式（与 getSeriesAndLegend 一致，PercentLine 等复用） */
export const LINE_CHART_SYMBOL_EMPHASIS: TSeriesEmphasis = {
  itemStyle: {
    borderColor: '#fff',
    borderWidth: 1,
    borderType: 'solid',
    opacity: 1,
  },
};

export type ISeriesItem = Omit<SeriesOption, 'data' | 'itemStyle' | 'emphasis'> & {
  itemStyle: TSeriesItemStyle;
  emphasis?: TSeriesEmphasis;
  data: TSeriesData[];
  valueType: string;
  metric?: string;
  yAxisIndex?: number;
};

// setSeriesItem 函数的参数类型
export interface ISetSeriesItemConfig {
  seriesIndex: number;
  isCompare: boolean;
  color: string; // 当前的颜色
}

// setSeriesItem 函数类型（允许返回扩展的 series 配置）
export type TSetSeriesItemFn<T extends ISeriesItem = ISeriesItem> = (
  seriesItem: Readonly<ISeriesItem>,
  config: ISetSeriesItemConfig
) => T;

// 函数参数
export interface IGetSeriesAndLegendParams<T extends ISeriesItem = ISeriesItem> {
  seriesData: ISeries[];
  setSeriesItem?: TSetSeriesItemFn<T>;
  isEn: boolean;
  displayConfig?: Record<string, TSeriesData['display']>;
  compareDateCount?: number;
  legendColors?: TLegendColors;
}

// 函数返回类型
export interface IGetSeriesAndLegendResult<T extends ISeriesItem = ISeriesItem> {
  series: T[];
  legendData: string[];
}

/**
 * 将后端折线图 series 数据转换为前端 ECharts series & legend 信息。
 * - 颜色 key 始终按“中文对照组名 + 图例名”构建，保证中英文切换时颜色映射稳定；
 * - 支持多对比日期场景，通过透明度区分 compare 曲线；
 * - 统一生成 tooltip 使用的 TSeriesData 结构。
 */
export const getSeriesAndLegend = <T extends ISeriesItem = ISeriesItem>(
  params: IGetSeriesAndLegendParams<T>,
): IGetSeriesAndLegendResult<T> => {
  const {
    seriesData = [],
    setSeriesItem,
    isEn,
    displayConfig,
    compareDateCount = 4,
    legendColors,
  } = params;

  // 单独计算 option其他固定配置
  const minAlpha = 1 / (compareDateCount + 1);
  const legendKeyToBaseColor: Record<string, string> = {};
  const legendKeyToCompareCount: Record<string, number> = {};

  const legendKeysInOrder: string[] = [];
  const legendKeySeen = new Set<string>();
  seriesData?.forEach((seriesItem: ISeries) => {
    const {
      control_name_cn: controlNameCn,
      name: seriesDataName,
    } = seriesItem || {};
    /**
     * 颜色 key 一律使用“中文对照组名 + 图例名”：
     * - 这样可以保证中英文切换时使用同一套 legendKey，不会因为语言变更导致颜色错乱；
     * - 展示文案后面会根据 isEn 单独拼接，不影响颜色映射。
     */
    const controlNameForKey = controlNameCn;
    const legendKey = buildLegendKey({ controlName: controlNameForKey, seriesDataName });
    if (!legendKey || legendKeySeen.has(legendKey)) return;
    legendKeySeen.add(legendKey);
    legendKeysInOrder.push(legendKey);
  });
  const legendKeyToPaletteIndex = buildLegendPaletteIndexMap(legendKeysInOrder);

  const controlNameSet = new Set<string>();
  const legend: string[] = [];
  const seriesArr: T[] = seriesData?.map((series: ISeries, index: number) => {
    const {
      data,
      metric,
      valueType,
      dimensions,
      date_range: dateRange, // [2025-08-29, 2025-09-03],
      // last_time: lastTime, // 上传参数一致,
      is_compare: isCompare, // 是否是对比日期
      // granularity, // 对比时间跨度的粒度
      // date_granularity: dateGranularity, // 对比时间所在的日期的粒度
      compare_time_name_cn: compareTimeNameCn,
      compare_time_name_en: compareTimeNameEn,
      name: seriesDataName,
      control_name_en: controlNameEn,
      control_name_cn: controlNameCn,
      is_others: isOthers,
      yAxisIndex,
    } = series;
    /**
     * 颜色 key 与展示文案分离：
     * - controlNameForKey：始终使用中文对照组名，保证 legendKey 在中英文下一致；
     * - controlNameDisplay：根据 isEn 选择中/英文，仅用于图例展示与“单对照组去前缀”。
     */
    const controlNameForKey = String(controlNameCn ?? '');
    const controlNameDisplay = String((isEn ? controlNameEn : controlNameCn) ?? '');
    controlNameSet.add(controlNameDisplay);
    const legendKey = buildLegendKey({
      controlName: controlNameForKey,
      seriesDataName: String(seriesDataName ?? ''),
    });
    // 1. 计算对比时间文本
    const compareText: string = (isEn ? compareTimeNameEn : compareTimeNameCn) || '';
    
    /**
     * 2. 计算name名称(图例名称)
     * seriesDataName 是后端返回的 name图例名称
     *
     *  维度   指标    对比时间   图例名称
     *  单     单      无        seriesDataName(指标名称(无tableCode前缀))
     *  单     多      无        seriesDataName(指标名称(无tableCode前缀))
     *  单     单      有        指标名称 + 对比日期名称
     *  单     多      有        指标名称 + 对比日期名称
     *
     *  多     单      无        seriesDataName(维度名称)
     *  多     多      无        seriesDataName(维度名称+指标名称(无tableCode前缀))
     *  多     单      有        维度名称 + 对比日期名称 (没有指标名称)
     *  多     多      有        维度名称 + 指标名称 + 对比日期名称
     */
    let name = seriesDataName || '';
    if (compareText) {
      // 说明有对比时间, 需要拼接
      name = `${seriesDataName}(${compareText})${DATALAB_SPLIT_STR}${index}`;
      // 拼接DATALAB_SPLIT_STR的原因是 在对比日期中, 设置了两个 Custom的对比日期时, name会出现重名, 图例展示时会去重, 导致缺少图例.
      // 这里拼接后, 保证唯一性, 然后在legend的formater中再拆分.
    }
    if (controlNameDisplay) { // 如果有对照组名称（展示用）
      name = `${controlNameDisplay},${name}`;
    }
    legend.push(name);
    // 3. 计算颜色
    let baseColor = legendKeyToBaseColor[legendKey];
    if (!baseColor) {
      const paletteIndex = legendKeyToPaletteIndex[legendKey] ?? 0;
      baseColor = findRgbaForLegend(legendKey, paletteIndex, legendColors);
      legendKeyToBaseColor[legendKey] = baseColor;
      legendKeyToCompareCount[legendKey] = 0;
    }

    let color = baseColor;
    if (isCompare) {
      const currentCompareCount = legendKeyToCompareCount[legendKey] ?? 0;
      const alpha = 1 - minAlpha * (currentCompareCount + 1);
      color = setRgbaAlpha(baseColor, alpha);
      legendKeyToCompareCount[legendKey] = currentCompareCount + 1;
    }
    
    // 4. 计算数据
    const normalizedDateRange: string[] = Array.isArray(dateRange)
      ? dateRange
      : (dateRange ? [dateRange] : []);
    const dataList = Array.isArray(data) ? data : [];
    const newData: TSeriesData[] = dataList.map((value: string | number, dataIndex: number): TSeriesData => {
      // 计算当前折线 在浮窗中 对比时间列的文本 (dateRange 是后端返回的对比时间的具体日期文本)
      // 如果浮窗中对比日期要求显示一个范围时, 后端返回的dateRange只有一个元素, 所以用dateRange?.[0]进行兜底
      const dateTextSource = normalizedDateRange[dataIndex] ?? normalizedDateRange[0] ?? '';
      const dateText: string = isCompare ? `${compareText}<br/>(${dateTextSource})` : dateTextSource;

      return {
        value,
        rawValue: value,
        metricName: metric?.replace(/(t[0-2]_)/, '') || '',
        metric,
        isPercent: valueType === 'percent',
        dimensions,
        dateText,
        valueType,
        isCompare,
        display: (displayConfig?.[metric] ?? {}) as TSeriesData['display'],
        controlName: controlNameDisplay,
        isOthers,
      };
    });

    const emphasis: TSeriesEmphasis = LINE_CHART_SYMBOL_EMPHASIS;

    const item: ISeriesItem = {
      name,
      itemStyle: {
        color,
      },
      emphasis,
      cursor: 'pointer',
      data: newData,
      valueType,
      metric,
      yAxisIndex,
    };
    
    if (typeof setSeriesItem === 'function') {
      return setSeriesItem(item, { seriesIndex: index, isCompare, color });
    }
    return item as T;
  });
  
  // 如果只有一个对照组, 则需要将图例中不显示对照组名称
  if (controlNameSet.size === 1) {
    const [name] = controlNameSet;
    if (name) { // 如果name不存在, 说明没有对照组
      for (let i = 0;i < legend.length;i++) {
        const newName = legend[i].slice(name.length+1);
        legend[i] = newName;
        // 同步修改 series 中的 name，确保图例能正确匹配
        if (seriesArr[i]) {
          seriesArr[i].name = newName;
        }
      }
    }
  }
  return { series: seriesArr, legendData: legend };
};

/**
 * 折线 ECharts 绘制值是否视为「无数据」。
 * 与 isNullDataPoint 对齐：null / undefined / '' / 'null' 均算空；合法 0 不算空。
 */
export function isEmptyLinePlottedValue(
  value: string | number | null | undefined,
): boolean {
  return (
    value === null
    || value === undefined
    || value === ''
    || value === 'null'
  );
}

/** 从 series.data 单点（原始值或 { value } 对象）解析 ECharts 绘制值 */
export function resolveLinePlottedValue(
  point: unknown,
): string | number | null | undefined {
  if (point === null || point === undefined) {
    return point;
  }
  if (typeof point === 'object' && point !== null && 'value' in point) {
    return (point as { value?: string | number | null | undefined; }).value;
  }
  if (typeof point === 'string' || typeof point === 'number') {
    return point;
  }
  return undefined;
}

/** 折线孤立点判定：series 内 data 点最小结构 */
export type TAloneSymbolDataPoint = {
  value?: string | number | null | undefined;
  itemStyle?: Record<string, unknown>;
};

export type TAloneSymbolSeries = {
  data?: TAloneSymbolDataPoint[];
};

/**
 * 孤立有效点显示圆点：两侧邻点均为空（含 null）时设 itemStyle.opacity=1。
 * 普通折线、事件折线、百分比折线共用。
 *
 * 调用方在 nullUi === 'ignore' 时应跳过：ignore 下 connectNulls 跨空值连线，
 * 若仍执行本函数，折线上会出现部分圆点显示（原孤立点）、部分不显示（连续段上的点）的混杂效果。
 */
export const setAloneSymbolOpacity = <T extends TAloneSymbolSeries>(
  series: T[],
): void => {
  series.forEach((seriesItem) => {
    seriesItem?.data?.forEach((
      item: TAloneSymbolDataPoint,
      index: number,
      sourceArr: TAloneSymbolDataPoint[],
    ) => {
      if (isEmptyLinePlottedValue(item.value)) {
        return;
      }
      const isAloneSymbol =
        isEmptyLinePlottedValue(sourceArr[index - 1]?.value)
        && isEmptyLinePlottedValue(sourceArr[index + 1]?.value);
      if (isAloneSymbol) {
        item.itemStyle = { ...(item.itemStyle ?? {}), opacity: 1 };
      }
    });
  });
};
